import * as React from 'react';

import { useQuery } from '@apollo/client/react';
import isEmpty from 'lodash/isEmpty';

import type {
  OnDemandLearnerMaterialsQuery,
  OnDemandLearnerMaterialsQueryVariables,
} from 'bundles/program-home/components/modals/__generated__/onDemandLearnerMaterialsQuery';
import type {
  ProgramCurriculumProductsQuery_ProgramCurriculumProductsV1Resource_enrolled_elements as Product,
  ProgramCurriculumProductsQuery_ProgramCurriculumProductsV1Resource_enrolled_elements_productState_ProgramCurriculumProductsV1_programCourseWithStateMember as ProgramCourseWithStateMember,
  ProgramCurriculumProductsQuery,
  ProgramCurriculumProductsQueryVariables,
} from 'bundles/program-home/components/modals/__generated__/programCurriculumProductsQuery';
import { apiConfig } from 'bundles/program-home/constants/ProgramHomeConfig';
import { COURSE_PROGRESS_THRESHOLD } from 'bundles/program-home/constants/forcedSSORecoveryEmail';
import onDemandLearnerMaterialsQuery from 'bundles/program-home/queries/onDemandLearnerMaterialsQuery';
import programCurriculumProductsQuery from 'bundles/program-home/queries/programCurriculumProductsQuery';

const { PROGRAM_PRODUCT_ENROLLED_DISPLAY_LIMIT } = apiConfig;

const useCheckSignificantCourseProgress = (programId: string) => {
  const { data: productsData } = useQuery<ProgramCurriculumProductsQuery, ProgramCurriculumProductsQueryVariables>(
    programCurriculumProductsQuery,
    {
      variables: {
        programId,
        limit: PROGRAM_PRODUCT_ENROLLED_DISPLAY_LIMIT,
      },
    }
  );

  const products = (productsData?.ProgramCurriculumProductsV1Resource?.enrolled?.elements ?? []) as Product[];

  const { data: learnerMaterialsData } = useQuery<
    OnDemandLearnerMaterialsQuery,
    OnDemandLearnerMaterialsQueryVariables
  >(onDemandLearnerMaterialsQuery, {
    skip: isEmpty(products),
    variables: {
      courseIds: products
        .filter(({ productState }) => Boolean((productState as ProgramCourseWithStateMember).productState?.courseId))
        .map(({ productState }) => (productState as ProgramCourseWithStateMember).productState.courseId),
    },
  });

  const learnerMaterialsArray = learnerMaterialsData?.OnDemandLearnerMaterialsV1Resource?.courses?.elements;

  const hasSignificantCourseProgress = React.useMemo(() => {
    return (
      !!learnerMaterialsArray &&
      !isEmpty(learnerMaterialsArray) &&
      learnerMaterialsArray.some((item) =>
        item?.summaryCoreProgressPercentage ? item.summaryCoreProgressPercentage > COURSE_PROGRESS_THRESHOLD : false
      )
    );
  }, [learnerMaterialsArray]);

  return hasSignificantCourseProgress;
};

export default useCheckSignificantCourseProgress;
